import { usePersonReadDataContext } from '@aibees/elephant/context';
import { useContactDetailFieldLoading } from '@aibees/elephant/hooks/people/use-contact-detail-field-loading';
import { Icon, Tab } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { TabList } from '@mui/lab';
import { Badge } from '@mui/material';
import { memo } from 'react';
import { tabsUpdatesMapper } from '../../config';
import {
  useContactDetailsConfig,
  useContactDetailsData,
  useContactDetailsState,
} from '../../context';
import {
  doesContactDetailHaveActionableUpdate,
  showProfileInsightItemTabBadge,
} from '../../utils';
import { ContactDetailsFailedContent } from '../contact-details-failed-content';
import * as Styled from './styles';
import * as SX from './sx';
import { ContactDetailTabsProps as Props } from './types';

export const ContactDetailTabs = memo(({ className }: Props) => {
  const { handleTabClick } = useContactDetailsConfig();
  const { failed, unlocked } = useContactDetailsState();
  const { sortedItemsWithContentCheck, hasJobChange } = useContactDetailsData();
  const { api } = usePersonReadDataContext();
  const { isFieldLoading } = useContactDetailFieldLoading(api.data);

  return (
    <Styled.Wrapper
      className={classnames('ContactDetailTabs', className)}
      sx={SX.root}
    >
      <TabList
        onChange={(event, newValue) => handleTabClick(newValue)}
        size="medium"
        variant="scrollable"
      >
        {sortedItemsWithContentCheck.map((x) => {
          const hasUpdate = doesContactDetailHaveActionableUpdate(
            tabsUpdatesMapper[x.key],
            api?.data
          );
          const fieldLoading = isFieldLoading(tabsUpdatesMapper[x.key]);
          const showBadge = showProfileInsightItemTabBadge(
            !!(!unlocked && x.disabled && !api.data?.revelation?.contact),
            x.key,
            hasJobChange,
            fieldLoading,
            hasUpdate
          );

          const tabContent = (
            <Tab
              key={x.key}
              icon={
                showBadge ? (
                  <Badge
                    size={16}
                    variant="soft"
                    color={hasUpdate || fieldLoading ? 'primary' : 'default'}
                    badgeContent={
                      fieldLoading ? (
                        <Icon name="update" sxx={SX.spinningIcon} size={12} />
                      ) : hasUpdate ? (
                        <Icon name="notifications-active" />
                      ) : x.disabled && !api.data?.revelation?.contact ? (
                        <Icon name="table-lock-filled" />
                      ) : null
                    }
                  >
                    <Icon size={20} name={x.icon ?? 'business'} />
                  </Badge>
                ) : (
                  <Icon size={20} name={x.icon ?? 'business'} />
                )
              }
              iconPosition="start"
              value={x.value}
            />
          );

          return tabContent;
        })}
      </TabList>

      {failed && <ContactDetailsFailedContent />}
    </Styled.Wrapper>
  );
});

ContactDetailTabs.displayName = 'ContactDetailTabs';
